import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { PageLoader } from '@/components/ui';
import { useAuth } from '@/features/auth/AuthProvider';
import type { Permission } from '@/features/auth/permissions';

// Route-level code splitting: each module loads on first visit.
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const TendersPage = lazy(() => import('@/pages/tenders/TendersPage'));
const TenderProfilePage = lazy(() => import('@/pages/tenders/TenderProfilePage'));
const VendorsPage = lazy(() => import('@/pages/vendors/VendorsPage'));
const QuantityPage = lazy(() => import('@/pages/quantity/QuantityPage'));
const FinancialsPage = lazy(() => import('@/pages/financials/FinancialsPage'));
const DocumentsPage = lazy(() => import('@/pages/documents/DocumentsPage'));
const ExpiringContractsPage = lazy(() => import('@/pages/expiring/ExpiringContractsPage'));
const ReportsPage = lazy(() => import('@/pages/reports/ReportsPage'));
const AuditLogsPage = lazy(() => import('@/pages/audit/AuditLogsPage'));
const UserManagementPage = lazy(() => import('@/pages/users/UserManagementPage'));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const AccountPage = lazy(() => import('@/pages/account/AccountPage'));
const MasterDataPage = lazy(() => import('@/pages/masterdata/MasterDataPage'));
const ExecutiveOverviewPage = lazy(() => import('@/pages/executive/ExecutiveOverviewPage'));
const AlertHistoryPage = lazy(() => import('@/pages/alerts/AlertHistoryPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading, loginCode } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoader label="Checking session…" />;
  if (!user && loginCode) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <>{children}</>;
}

function Guard({ permission, children }: { permission: Permission; children: ReactNode }) {
  const { can } = useAuth();
  return can(permission) ? <>{children}</> : <NotFoundPage forbidden />;
}

/** Executives (BU Head / CGM) land on the Executive overview; everyone else on the Dashboard. */
function HomeRedirect() {
  const { user } = useAuth();
  return <Navigate to={user?.role === 'executive' ? '/executive' : '/dashboard'} replace />;
}

export function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route index element={<HomeRedirect />} />
          <Route path="executive" element={<Guard permission="executive.view"><ExecutiveOverviewPage /></Guard>} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="tenders" element={<TendersPage />} />
          <Route path="tenders/:tenderId" element={<TenderProfilePage />} />
          <Route path="vendors" element={<VendorsPage />} />
          <Route path="quantity" element={<QuantityPage />} />
          <Route path="financials" element={<FinancialsPage />} />
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="expiring" element={<ExpiringContractsPage />} />
          {/* The Approvals page was removed: old links (e.g. notifications) go to the dashboard. */}
          <Route path="approvals" element={<Navigate to="/dashboard" replace />} />
          <Route path="reports" element={<Guard permission="report.export"><ReportsPage /></Guard>} />
          <Route path="alerts" element={<AlertHistoryPage />} />
          <Route path="audit-logs" element={<Guard permission="audit.view"><AuditLogsPage /></Guard>} />
          <Route path="master-data" element={<Guard permission="vendor.manage"><MasterDataPage /></Guard>} />
          <Route path="users" element={<Guard permission="user.manage"><UserManagementPage /></Guard>} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="account" element={<AccountPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
